import { useEffect, useReducer, useState } from "react";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import { selectIsLoggedIn, setProfileDialogShown, setIsShowingProfileDialog, responsesUpdated } from "../redux/authSlice";
import MutipleChoiceQuestion from "./MultipleChoiceQuestion";
import NumericQuestion from "./NumericQuestion";
import { formatPercent } from "../helpers/utils";
import ProgressDots from "./ProgressDots/ProgressDots";
import { Snackbar, Alert } from "@mui/material";



const reducer = (state, action) => {

	switch (action.type) {
		case ACTION.NEW_QUESTIONS:
			const questions = [];
			const showInsightsTour = action.payload.showInsightsTour;
			const showBenchmarkingTour = action.payload.showBenchmarkingTour;
			let profileComplete = true;
			for (let question of action.payload.questions) {
				if ((question.responses === undefined) || (question.responses.length === 0)) {
					profileComplete = false;
					questions.push(structuredClone(question));
				}
			}
			return { ...state, questions, dataLoaded: true, profileComplete, showInsightsTour, showBenchmarkingTour };
		case ACTION.NEW_RESPONSES:
			const surveyQuestionId = action.payload.surveyQuestionId;
			const newQuestions = structuredClone(state.questions);
			for (let question of newQuestions) {
				if (question.surveyQuestionId === surveyQuestionId) {
					question.responses = [...action.payload.responses];
				}
			}
			return { ...state, questions: newQuestions };
		default:
			console.log(`unhandled action.type: ${action.type}`)
	}
}

const ACTION = {
	NEW_QUESTIONS: "newQuestions",
	NEW_RESPONSES: "newResponses",
}

const ProfileDialog = () => {

	// console.log(`ProfileDialog: render at ${new Date().getTime()}`);

	const axiosPrivate = useAxiosPrivate();

	const reduxDispatch = useDispatch();

	const isLoggedIn = useSelector(selectIsLoggedIn);

	const [state, dispatch] = useReducer(reducer, {
		showInsightsTour: false,
		showBenchmarkingTour: false,
		questions: [],
		profileComplete: false,
		dataLoaded: false
	});

	const location = useLocation();
	const path = location.pathname;

	const [index, setIndex] = useState(0);

	const [showWizard, setShowWizard] = useState(false);

	const [alertStatus, setAlertStatus] = useState({
		open: false,
		severity: null,
		message: null,
	});


	useEffect(() => {
		let
			show;

		show = false;

		if (path) {
			if (state.dataLoaded) {
				if (!state.profileComplete) {
					if (((path === '/') || (path === '/benchmarking') || (path === '/benchmarking/')) && !state.showBenchmarkingTour) {
						show = true;
					} else if ((path === '/insights' || path === '/insights/') && !state.showInsightsTour) {
						show = true;
					}
				}
			}
		}
		setShowWizard(show);
	}, [path, state.dataLoaded, state.profileComplete, state.showInsightsTour, state.showBenchmarkingTour]);



	useEffect(() => {
		if (state.profileComplete) {
			reduxDispatch(setIsShowingProfileDialog(false));
		}
	}, [state.profileComplete, reduxDispatch])

	useEffect(() => {

		if (!isLoggedIn) {
			return;
		}

		let isMounted = true;
		const controller = new AbortController();

		const getUser = async () => {
			// console.log(`ProfileDialog: getUser at ${new Date().getTime()}`);
			try {
				const response = await axiosPrivate.get('/profile', {
					signal: controller.signal
				});
				if (isMounted) {
					dispatch({
						type: ACTION.NEW_QUESTIONS, payload: response.data
					});
				}
			} catch (err) {
				if (isMounted) {
					console.error(err);
				}
			}
		}

		getUser();

		return () => {
			isMounted = false;
			controller.abort();
		}
	}, [isLoggedIn, axiosPrivate])

	const saveResponse = async (params) => {

		const json = { ...params };

		try {
			const response = await axiosPrivate.post(`/surveyQuestions/${params.surveyQuestionId}`,
				JSON.stringify(json),
				{
					headers: { 'Content-Type': 'application/json' }
				}
			);
			dispatch({
				type: ACTION.NEW_RESPONSES, payload: { ...response.data }
			});
			reduxDispatch(responsesUpdated());
            setAlertStatus(prevState => ({
                ...prevState,
                severity: "success",
                message: "Response saved",
                open: true
            }));
		} catch (err) {
			console.log(err);
		}
	}

	const questions = state.questions.length;
	const screens = 2;
	const dialogWidth = 600;
	const dialogWidthPx = `${dialogWidth}px`;

	const dialogContainer = {
		width: dialogWidthPx,
	}

	const screensContainer = {
		width: `${formatPercent(screens * 100, 0)}`,
		transform: `translateX(${formatPercent(-100 * (index < questions ? 0 : 1) / screens, 2)}`
	}
	const screenContainer = {
		width: dialogWidthPx
	}

	const questionsContainer = {
		width: `${(questions * 100)}%`,
		transform: `translateX(${formatPercent(-100 * Math.min(index, questions - 1) / questions, 2)}`
	}
	const questionContainer = {
		width: dialogWidthPx
	}

	const showPrevQuestion = () => {
		setIndex((prev) => Math.max(0, prev - 1));
	}
	const showNextQuestion = () => {
		setIndex((prev) => Math.min(questions, prev + 1));
	}

	const closeDialog = () => {
		reduxDispatch(setProfileDialogShown());
		reduxDispatch(setIsShowingProfileDialog(false));
	}

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus(prevState => ({
            ...prevState,
            open: false
        }));
    };

	return (
		showWizard &&
		<div className="modal dark">
			<div className="onboard-wizard-dialog" style={dialogContainer}>
				<h2>Complete Your Profile</h2>
				<div className="wizard-screens-container" style={screensContainer}>
					<div className="wizard-screen-container" style={screenContainer}>
						<div style={{ padding: "0 2rem" }}>
							<p>Before diving in, please answer a few brief questions about you and your practice:</p>
						</div>
						<div className="wizard-questions-container" style={questionsContainer}>
							{
								state.questions.map((question, i) => {
									switch (question.surveyQuestionType) {
										case "multiple choice":
											return (
												<div key={i} className="wizard-question-container" style={questionContainer}>
													<ol className="title">
														<li value={i + 1}>{question.text}</li>
													</ol>
													<MutipleChoiceQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />
												</div>
											)
										case "numeric":
											return (
												<div key={i} className="wizard-question-container" style={questionContainer}>
													<ol className="title">
														<li value={i + 1}>{question.text}</li>
													</ol>
													<NumericQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />
												</div>
											)
										default:
											return null
									}
								})
							}
						</div>
					</div>
					<div className="wizard-screen-container" style={screenContainer}>
						<div style={{ padding: "0 2rem" }}>
							<p>Thank you.</p>
							<p>If you'd like to update your responses in the future, they're available in your profile, accesible via the button in the upper right corner.</p>
							<button className="arrowed" onClick={closeDialog}>Get started</button>
						</div>
					</div>
				</div>
				<div className="toolbar">
					<button className="link" onClick={showPrevQuestion} disabled={index <= 0}>&lt; Back</button>
					<ProgressDots totalSteps={questions + 1} currentStep={index + 1} />
					<button className="link" onClick={showNextQuestion} disabled={index >= questions}>Next &gt;</button>
				</div>
				<button className="close" onClick={closeDialog}></button>
				<Snackbar open={alertStatus.open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
					<Alert onClose={handleClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
				</Snackbar>
			</div >
		</div>
	)
}

export default ProfileDialog